import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { syncEngine } from '../features/sync/engine';
import { firstJoin } from '../features/sync/firstJoin';
import { getSyncClient } from '../features/sync/client';
import { SyncAuthError } from '../features/sync/types';
import { useDeviceStore } from '../store/device';

// Sync settings page per plan §10. Renders one of three states:
//
//   * disconnected → input form to enter a code.
//   * connected    → show the masked code + "Irrota" button.
//   * auth-lost    → banner explaining the connection broke, with the
//                    same input form below it.
//
// First-join (replace local with server snapshot) runs from inside the
// "connect" handler so the device joins an existing family cleanly. v1
// has no in-app code rotation — that's a v2 follow-up per plan/02 Q2.

const HIDDEN = '••••••••';

export function SyncPage() {
  const navigate = useNavigate();
  const syncCode = useDeviceStore((s) => s.syncCode);
  const setSyncCode = useDeviceStore((s) => s.setSyncCode);
  const syncAuthLost = useDeviceStore((s) => s.syncAuthLost);
  const setSyncAuthLost = useDeviceStore((s) => s.setSyncAuthLost);

  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<'success' | 'error' | null>(null);

  const onConnect = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = input.trim();
    if (!code) return;
    setPending(true);
    setError(null);
    let client = null;
    try {
      client = await getSyncClient();
      // Auth first so the family record is resolved; firstJoin reads
      // currentFamilyId() off the client.
      await client.authWithSyncCode(code);
      await firstJoin(client);
      // Now hand off to the engine for the live subscription path.
      await syncEngine.start({ syncCode: code });
      setSyncCode(code);
      setSyncAuthLost(false);
      navigate('/settings');
    } catch (err) {
      // Drop any JWT the SDK may have persisted during a partial-success
      // connect (e.g. authWithSyncCode succeeded but engine.start fell
      // over). Leaving it would let isAuthed() lie on the next boot.
      client?.authClear();
      if (err instanceof SyncAuthError) {
        setError('Koodia ei tunnistettu.');
      } else {
        setError('Yhteys epäonnistui. Yritä myöhemmin uudelleen.');
        console.warn('sync connect failed', err);
      }
    } finally {
      setPending(false);
    }
  };

  const onDisconnect = async () => {
    await syncEngine.stop();
    setSyncCode(null);
    setSyncAuthLost(false);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setRefreshStatus(null);
    try {
      const result = await syncEngine.recover();
      setRefreshStatus(result.kind === 'recovered' ? 'success' : 'error');
    } catch (err) {
      console.warn('sync refresh failed', err);
      setRefreshStatus('error');
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshStatus(null), 3000);
    }
  };

  const isConnected = syncCode !== null && !syncAuthLost;

  return (
    <main className="settings">
      <Link to="/settings" className="back-button" aria-label="Takaisin asetuksiin">
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="settings-title">Synkronointi</h1>

      {syncAuthLost && (
        <section className="settings-section sync-auth-lost-banner" role="alert">
          <p className="settings-error">Yhteys katkesi — anna koodi uudelleen.</p>
        </section>
      )}

      {isConnected ? (
        <section className="settings-section">
          <h2 className="settings-section-title">Yhteyden tila</h2>
          <p className="settings-hint">Tämä laite on yhdistetty perheen tilille.</p>
          <div className="sync-code-display">
            <span aria-label="Perhekoodi">{revealed ? syncCode : HIDDEN}</span>
            <button
              type="button"
              className="settings-button"
              onClick={() => setRevealed((r) => !r)}
            >
              {revealed ? 'Piilota' : 'Näytä'}
            </button>
          </div>
          <p className="settings-hint">
            Koodin vaihto: pyydä perheen ylläpitäjää päivittämään koodi PocketBase-konsolista.
          </p>
          <button
            type="button"
            className="settings-button"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Päivitetään…' : 'Päivitä nyt'}
          </button>
          {refreshStatus === 'success' && (
            <p className="settings-hint" role="status">
              Synkronointi päivitetty.
            </p>
          )}
          {refreshStatus === 'error' && (
            <p className="settings-error" role="alert">
              Päivitys epäonnistui. Yritä hetken kuluttua uudelleen.
            </p>
          )}
          <button
            type="button"
            className="settings-button settings-button-danger"
            onClick={onDisconnect}
          >
            Irrota tästä laitteesta
          </button>
        </section>
      ) : (
        <section className="settings-section">
          <h2 className="settings-section-title">Yhdistä</h2>
          <p className="settings-hint">
            Anna perhekoodi, jonka olet saanut ylläpitäjältä. Tämän jälkeen rutiinit synkronoituvat
            automaattisesti perheen muiden laitteiden kanssa.
          </p>
          <p className="settings-hint settings-warn" role="note">
            Yhdistäminen korvaa tämän laitteen paikalliset profiilit ja rutiinit perheen
            synkronoidulla tiedolla.
          </p>
          <form className="sync-form" onSubmit={onConnect}>
            <label className="sync-form-label">
              <span>Perhekoodi</span>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                aria-label="Perhekoodi"
                placeholder="esim. KARHU42"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                disabled={pending}
              />
            </label>
            <button type="submit" className="settings-button" disabled={pending || !input.trim()}>
              {pending ? 'Yhdistetään…' : 'Yhdistä'}
            </button>
            {error && (
              <p className="settings-error" role="alert">
                {error}
              </p>
            )}
          </form>
        </section>
      )}
    </main>
  );
}
